import { ThemeProvider } from "next-themes";
import { WyngThemeProvider } from "@/contexts/WyngThemeProvider";
import { LoyaltyProvider } from "@/contexts/LoyaltyProvider";
import { TwinProvider } from "@/contexts/TwinProvider";
import SecondWingCelebration from "@/components/SecondWingCelebration";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/auth/AuthContext";
import ProtectedRoute from "@/auth/ProtectedRoute";
import BottomNav from "@/components/BottomNav";
import { Suspense, lazy } from "react";

// EAGER pages — must be in the main bundle so the first paint after
// login/welcome doesn't trigger a network round-trip for the JS:
//   · Welcome / Login / Register → bounce in & out fast
//   · Dashboard                  → first protected page everybody lands on
import Welcome from "@/pages/Welcome";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
// MagicConsume MUST be eager-imported (not React.lazy). On iOS Capacitor
// cold-launch via a Universal Link, the bundled JS resolves the route
// to /auth/magic/:token before WKWebView has a chance to load the lazy
// chunk for it. The lazy fetch then stalls or fails silently inside the
// `capacitor://localhost` origin, leaving the user on a black screen
// (Suspense fallback) forever. Eager-loading the ~5 kB MagicConsume
// component costs nothing on first paint but guarantees it renders
// during the cold-launch race. (Recommended by support, Jun 2026.)
import MagicConsume from "@/pages/MagicConsume";
const InviteLanding = lazy(() => import("@/pages/InviteLanding"));

// LAZY everything else. React.lazy + Suspense splits each page into
// its own JS chunk that's only downloaded when the route is hit.
// This shrinks the initial bundle from ≈3 MB → ≈700 kB on a cold
// install — Settings/Shadow/CrewMap stop blocking the first paint.
const ForgotPassword       = lazy(() => import("@/pages/ForgotPassword"));
const Referrals            = lazy(() => import("@/pages/Referrals"));
const Plan                 = lazy(() => import("@/pages/Plan"));
const Admin                = lazy(() => import("@/pages/Admin"));
const Onboarding           = lazy(() => import("@/pages/Onboarding"));
const AvatarStudio         = lazy(() => import("@/pages/AvatarStudio"));
const ShadowBattle         = lazy(() => import("@/pages/ShadowBattle"));
const TwinBattle           = lazy(() => import("@/pages/TwinBattle"));
const HeroBattle           = lazy(() => import("@/pages/HeroBattle"));
const ScreenTime           = lazy(() => import("@/pages/ScreenTime"));
const HallOfHeroes         = lazy(() => import("@/pages/HallOfHeroes"));
const Nutrition            = lazy(() => import("@/pages/Nutrition"));
const AddFood              = lazy(() => import("@/pages/AddFood"));
const Coach                = lazy(() => import("@/pages/Coach"));
const Social               = lazy(() => import("@/pages/Social"));
const Settings             = lazy(() => import("@/pages/Settings"));
const Billing              = lazy(() => import("@/pages/Billing"));
const ImportData           = lazy(() => import("@/pages/ImportData"));
const Friends              = lazy(() => import("@/pages/Friends"));
const FriendsAccept        = lazy(() => import("@/pages/FriendsAccept"));
const Integrations         = lazy(() => import("@/pages/Integrations"));
const IntegrationsCallback = lazy(() => import("@/pages/IntegrationsCallback"));
const MetricDetail         = lazy(() => import("@/pages/MetricDetail"));
// Challenges page restored as an overview (Mijn challenges + Beschikbaar)
// after the UAT redesign. The legacy multi-mode page is still mounted
// for the wizard via `/challenges?opponents=...`.
const Challenges            = lazy(() =>
    import("@/pages/Challenges").then((m) => ({ default: m.default })));
const ChallengesOverview    = lazy(() => import("@/pages/ChallengesOverview"));
const ChallengeLeaderboard  = lazy(() =>
    import("@/pages/Challenges").then((m) => ({ default: m.ChallengeLeaderboard })));
const ChallengeMembers      = lazy(() => import("@/pages/ChallengeMembers"));
const ChallengeDetail       = lazy(() => import("@/pages/ChallengeDetail"));
const ChallengeLeaderboardFull = lazy(() => import("@/pages/ChallengeLeaderboardFull"));
const SelectFriendsPage     = lazy(() => import("@/pages/SelectFriendsPage"));
const SelectFofPage         = lazy(() => import("@/pages/SelectFofPage"));
const EventParticipants     = lazy(() => import("@/pages/EventParticipants"));
const StepsHistory          = lazy(() => import("@/pages/StepsHistory"));
const InviteAccept          = lazy(() => import("@/pages/InviteAccept"));
const JoinChallenge         = lazy(() => import("@/pages/JoinChallenge"));
const Chats                 = lazy(() => import("@/pages/Chats"));
const ChatRoom              = lazy(() => import("@/pages/ChatRoom"));
const ChallengeChatPage     = lazy(() => import("@/pages/ChallengeChatPage"));
const MessageInfo           = lazy(() => import("@/pages/MessageInfo"));
const HealthSources        = lazy(() => import("@/pages/HealthSources"));
const MetricManagement     = lazy(() => import("@/pages/MetricManagement"));
const AuthCallback         = lazy(() => import("@/pages/AuthCallback"));
const Account              = lazy(() => import("@/pages/Account"));
const StickerCreator       = lazy(() => import("@/pages/StickerCreator"));
const Promo                = lazy(() => import("@/pages/Promo"));
const ActivityDetail       = lazy(() => import("@/pages/ActivityDetail"));
const PrivacyLegal         = lazy(() =>
    import("@/pages/Legal").then((m) => ({ default: m.Privacy })));
const Terms                = lazy(() =>
    import("@/pages/Legal").then((m) => ({ default: m.Terms })));
const Privacy              = lazy(() => import("@/pages/Privacy"));
const PrivacyDataCenter    = lazy(() => import("@/pages/PrivacyDataCenter"));
const CoachingStyle        = lazy(() => import("@/pages/CoachingStyle"));
const NoGuessing           = lazy(() => import("@/pages/NoGuessing"));
const Grocery              = lazy(() => import("@/pages/Grocery"));
const GroceryNew           = lazy(() => import("@/pages/GroceryNew"));
const GroceryAiWeek        = lazy(() => import("@/pages/GroceryAiWeek"));
const GroceryDetail        = lazy(() => import("@/pages/GroceryDetail"));
const GroceryShopMode      = lazy(() => import("@/pages/GroceryShopMode"));
const Crews                = lazy(() => import("@/pages/Crews"));
const CrewNew              = lazy(() => import("@/pages/CrewNew"));
const CrewDetail           = lazy(() => import("@/pages/CrewDetail"));
const CrewJoin             = lazy(() => import("@/pages/CrewJoin"));
const BadgePreview         = lazy(() => import("@/pages/BadgePreview"));
const Badges               = lazy(() => import("@/pages/Badges"));
const BadgeDetail          = lazy(() => import("@/pages/BadgeDetail"));
const Changelog            = lazy(() => import("@/pages/Changelog"));
const AdminFinance         = lazy(() => import("@/pages/AdminFinance"));
const SettingsTours        = lazy(() => import("@/pages/SettingsTours"));
const WingsShop            = lazy(() => import("@/pages/WingsShop"));
const BadgeCreate          = lazy(() => import("@/pages/BadgeCreate"));
const BadgeCreated         = lazy(() => import("@/pages/BadgeCreated"));
const Gift                 = lazy(() => import("@/pages/Gift"));
const DataExport           = lazy(() => import("@/pages/DataExport"));
const CrewMap              = lazy(() => import("@/pages/CrewMap"));
const SettingsGeo          = lazy(() => import("@/pages/SettingsGeo"));
const DiagnosticsPage      = lazy(() => import("@/pages/DiagnosticsPage"));
const DashboardCustomize   = lazy(() => import("@/pages/DashboardCustomize"));
const Diagnostics          = lazy(() => import("@/pages/Diagnostics"));

import ScrollToTop from "@/components/ScrollToTop";
import PageTransition from "@/components/PageTransition";
import OfflineIndicator from "@/components/OfflineIndicator";
import BackgroundServices from "@/components/BackgroundServices";
import WhatsNewModal from "@/components/WhatsNewModal";
import SpotlightTour from "@/components/spotlight/SpotlightTour";
import NativeBootstrap from "@/components/NativeBootstrap";
import UniversalLinksBootstrap from "@/components/UniversalLinksBootstrap";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import RouteTracker from "@/components/RouteTracker";
import { installGlobalErrorHandlers, diagLog } from "@/lib/diagnostics";

// Install error/rejection hooks before React mounts so even errors
// during initial chunk-load are captured.
installGlobalErrorHandlers();
import CreditsGate from "@/components/CreditsGate";

import "@/App.css";
import "@/i18n";

// Register service worker for push (no-op if unsupported)
if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => {});
    });
}

function App() {
    return (
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={true}>
            <AuthProvider>
                <WyngThemeProvider>
                <LoyaltyProvider>
                <TwinProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <RouteTracker />
                    <NavLoopGuardMount />
                    <UniversalLinksBootstrap />
                    <OfflineIndicator />
                    <BackgroundServices />
                    <PageTransition>
                    {/* Suspense boundary for the lazy-loaded routes. The
                        fallback is intentionally minimal — a plain
                        obsidian shell — so a 100-200 ms chunk fetch
                        doesn't flash a spinner. Most pages have their
                        own skeleton inside the page that takes over
                        as soon as the chunk resolves. */}
                    <Suspense fallback={<div className="app-shell" />}>
                    <AppErrorBoundary>
                    <Routes>
                        <Route path="/" element={<Login />} />
                        <Route path="/welcome" element={<Welcome />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/auth/magic/:token" element={<MagicConsume />} />
                        <Route path="/invite/:token" element={<InviteLanding />} />
                        <Route path="/gift/:token" element={<Gift />} />
                        {/* Legacy password flows kept reachable for direct
                            URL access (admin / support) but no longer
                            linked from the user-facing UI as of 1 Jun 2026. */}
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                        <Route path="/activity" element={<ProtectedRoute><ActivityDetail /></ProtectedRoute>} />
                        <Route path="/nutrition" element={<ProtectedRoute><Nutrition /></ProtectedRoute>} />
                        <Route path="/nutrition/add" element={<ProtectedRoute><AddFood /></ProtectedRoute>} />
                        <Route path="/nutrition/no-guessing" element={<ProtectedRoute><NoGuessing /></ProtectedRoute>} />
                        <Route path="/grocery" element={<ProtectedRoute><Grocery /></ProtectedRoute>} />
                        <Route path="/grocery/new" element={<ProtectedRoute><GroceryNew /></ProtectedRoute>} />
                        <Route path="/grocery/ai-week" element={<ProtectedRoute><GroceryAiWeek /></ProtectedRoute>} />
                        <Route path="/grocery/:id" element={<ProtectedRoute><GroceryDetail /></ProtectedRoute>} />
                        <Route path="/grocery/:listId/shop" element={<ProtectedRoute><GroceryShopMode /></ProtectedRoute>} />
                        <Route path="/crews" element={<ProtectedRoute><Crews /></ProtectedRoute>} />
                        <Route path="/crews/new" element={<ProtectedRoute><CrewNew /></ProtectedRoute>} />
                        <Route path="/crews/join/:token" element={<ProtectedRoute><CrewJoin /></ProtectedRoute>} />
                        <Route path="/crews/:id" element={<ProtectedRoute><CrewDetail /></ProtectedRoute>} />
                        <Route path="/badges/preview" element={<ProtectedRoute><BadgePreview /></ProtectedRoute>} />
                        <Route path="/badges" element={<ProtectedRoute><Badges /></ProtectedRoute>} />
                        <Route path="/badges/:id" element={<ProtectedRoute><BadgeDetail /></ProtectedRoute>} />
                        <Route path="/coach" element={<ProtectedRoute><Coach /></ProtectedRoute>} />
                        <Route path="/social" element={<ProtectedRoute><Social /></ProtectedRoute>} />
                        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                        <Route path="/settings/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                        <Route path="/settings/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
                        <Route path="/integrations/callback" element={<ProtectedRoute><IntegrationsCallback /></ProtectedRoute>} />
                        <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
                        <Route path="/friends/accept" element={<ProtectedRoute><FriendsAccept /></ProtectedRoute>} />
                        <Route path="/import" element={<ProtectedRoute><ImportData /></ProtectedRoute>} />
                        <Route path="/metric/:metric" element={<ProtectedRoute><MetricDetail /></ProtectedRoute>} />
                        {/* Release 2 — Chats tab. Naked /challenges
                            redirects to /chats. Deep-link wizard
                            (`/challenges?opponents=u1,u2`) keeps the
                            old page so existing flows from Crew Map
                            and the new "+battle in chat" CTA still work. */}
                        <Route path="/challenges" element={<ChallengesEntry />} />
                        <Route path="/chats" element={<ProtectedRoute><Chats /></ProtectedRoute>} />
                        <Route path="/chats/:id" element={<ProtectedRoute><ChatRoom /></ProtectedRoute>} />
                        <Route path="/chats/:roomId/info/:messageId" element={<ProtectedRoute><MessageInfo /></ProtectedRoute>} />
                        <Route path="/challenges/:id" element={<ProtectedRoute><ChallengeDetail /></ProtectedRoute>} />
                        <Route path="/challenges/:id/chat" element={<ProtectedRoute><ChallengeChatPage /></ProtectedRoute>} />
                        <Route path="/challenges/:roomId/info/:messageId" element={<ProtectedRoute><MessageInfo /></ProtectedRoute>} />
                        <Route path="/challenges/:id/members" element={<ProtectedRoute><ChallengeMembers /></ProtectedRoute>} />
                        <Route path="/challenges/:id/leaderboard" element={<ProtectedRoute><ChallengeLeaderboardFull /></ProtectedRoute>} />
                        <Route path="/events/new/select-friends" element={<ProtectedRoute><SelectFriendsPage /></ProtectedRoute>} />
                        <Route path="/events/new/select-fof" element={<ProtectedRoute><SelectFofPage /></ProtectedRoute>} />
                        <Route path="/events/:id/participants" element={<ProtectedRoute><EventParticipants /></ProtectedRoute>} />
                        <Route path="/steps/history" element={<ProtectedRoute><StepsHistory /></ProtectedRoute>} />
                        <Route path="/invite/:token" element={<InviteLanding />} />
                        <Route path="/i/:token" element={<InviteAccept />} />
                        <Route path="/join/:code" element={<ProtectedRoute><JoinChallenge /></ProtectedRoute>} />
                        <Route path="/onboarding/health-sources" element={<ProtectedRoute><HealthSources /></ProtectedRoute>} />
                        <Route path="/onboarding/metrics" element={<ProtectedRoute><MetricManagement /></ProtectedRoute>} />
                        <Route path="/onboarding/avatar" element={<ProtectedRoute><AvatarStudio /></ProtectedRoute>} />
                        <Route path="/battles/shadow" element={<ProtectedRoute><TwinBattle /></ProtectedRoute>} />
                        <Route path="/battles/shadow-classic" element={<ProtectedRoute><ShadowBattle /></ProtectedRoute>} />
                        <Route path="/battles/twin" element={<ProtectedRoute><TwinBattle /></ProtectedRoute>} />
                        <Route path="/twin/trophies" element={<Navigate to="/wyngx" replace />} />
                        <Route path="/battles/hero/:heroId" element={<ProtectedRoute><HeroBattle /></ProtectedRoute>} />
                        <Route path="/focus" element={<ProtectedRoute><ScreenTime /></ProtectedRoute>} />
                        <Route path="/hall-of-heroes" element={<ProtectedRoute><HallOfHeroes /></ProtectedRoute>} />
                        <Route path="/settings/metrics" element={<ProtectedRoute><MetricManagement /></ProtectedRoute>} />
                        <Route path="/settings/dashboard" element={<ProtectedRoute><DashboardCustomize /></ProtectedRoute>} />
                        <Route path="/settings/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                        <Route path="/stickers/create" element={<ProtectedRoute><StickerCreator /></ProtectedRoute>} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/promo" element={<Promo />} />
                        <Route path="/privacy" element={<PrivacyLegal />} />
                        <Route path="/settings/privacy" element={<ProtectedRoute><PrivacyDataCenter /></ProtectedRoute>} />
                        <Route path="/settings/privacy/legacy" element={<ProtectedRoute><Privacy /></ProtectedRoute>} />
                        <Route path="/settings/coaching-style" element={<ProtectedRoute><CoachingStyle /></ProtectedRoute>} />
                        {/* /future-you, /settings/theme, /rescue → archived for v1 launch.
                            See /app/_archive/ for the original code. */}
                        <Route path="/settings/changelog" element={<ProtectedRoute><Changelog /></ProtectedRoute>} />
                        <Route path="/settings/tours" element={<ProtectedRoute><SettingsTours /></ProtectedRoute>} />
                        <Route path="/settings/diagnostics" element={<ProtectedRoute><DiagnosticsPage /></ProtectedRoute>} />
                        <Route path="/wings-shop" element={<ProtectedRoute><WingsShop /></ProtectedRoute>} />
                        <Route path="/wyngx" element={<ProtectedRoute><WingsShop /></ProtectedRoute>} />
                        {/* Territory has been folded into the WYNG MAP itself
                            (see CrewMap → Hexagon toggle). The standalone
                            page used to have its own start/stop session UI
                            which Michel wanted removed. We keep a redirect
                            so any old deep-links still land somewhere sane. */}
                        <Route path="/territory" element={<Navigate to="/crew-map" replace />} />
                        <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
                        {/* /plan and /agenda were the Outlook-ish calendar tab.
                            As of Feb 2026 they redirect to /challenges — the new
                            hero tab that unifies challenges + meetups. Keeping
                            the redirect so push-notif deep-links + shared URLs
                            from older builds don't 404. */}
                        <Route path="/plan" element={<Navigate to="/challenges" replace />} />
                        <Route path="/plan/*" element={<Navigate to="/challenges" replace />} />
                        <Route path="/agenda" element={<Navigate to="/challenges" replace />} />
                        <Route path="/agenda/*" element={<Navigate to="/challenges" replace />} />
                        <Route path="/badges/create" element={<ProtectedRoute><BadgeCreate /></ProtectedRoute>} />
                        <Route path="/badges/created" element={<ProtectedRoute><BadgeCreated /></ProtectedRoute>} />
                        <Route path="/badges/created/:badgeId" element={<ProtectedRoute><BadgeCreated /></ProtectedRoute>} />
                        <Route path="/crew-map" element={<ProtectedRoute><CrewMap /></ProtectedRoute>} />
                        <Route path="/settings/geo" element={<ProtectedRoute><SettingsGeo /></ProtectedRoute>} />
                        <Route path="/settings/export" element={<ProtectedRoute><DataExport /></ProtectedRoute>} />
                        <Route path="/admin/finance" element={<ProtectedRoute><AdminFinance /></ProtectedRoute>} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/diagnostics" element={<Diagnostics />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </AppErrorBoundary>
                    </Suspense>
                    </PageTransition>
                    <BottomNav />
                    <WhatsNewModal />
                    <SpotlightTour />
                    <NativeBootstrap />
                    <CreditsGate />
                    <Toaster position="top-center" richColors theme="system" />
                    <SecondWingCelebration />
                </BrowserRouter>
                </TwinProvider>
                </LoyaltyProvider>
                </WyngThemeProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;

// NavLoopGuardMount — must live INSIDE <BrowserRouter> so it can call
// `useLocation()`. Keeps the router tree flat while still giving us
// the runtime tripwire. See `/app/frontend/src/lib/navGuard.js`.
function NavLoopGuardMount() {
    // Deferred import ensures the guard is tree-shakable if we ever
    // want to disable it in a build (e.g. Playwright CI). Currently
    // always on.
    // eslint-disable-next-line global-require
    const { useNavLoopGuard } = require("@/lib/navGuard");
    useNavLoopGuard();
    return null;
}

// Tiny routing helper — `/challenges` shows the overview (Mijn challenges
// + Beschikbaar) by default. Only when a `?opponents=` query param is
// present do we mount the legacy multi-mode page which auto-opens the
// create-challenge wizard pre-populated with those user IDs.
function ChallengesEntry() {
    const search = new URLSearchParams(window.location.search);
    if (search.get("opponents")) {
        return <ProtectedRoute><Challenges /></ProtectedRoute>;
    }
    return <ProtectedRoute><ChallengesOverview /></ProtectedRoute>;
}
